import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import { TProjectItem } from '@core/interfaces/TProjectItem';
import { AllowedRoleDirective } from '@shared/directives/allowed-role.directive';

import { PanelTabsComponent } from '@shared/components/content-panel';
import { ProjectsService } from '@shared/services/projects.service';

import { projectTab } from '../../../../enums/tab';
import { ProjectBotsComponent } from '../../../projects/project-bots/project-bots.component';
import { ProjectSourcesComponent } from '../../../projects/project-sources/project-sources.component';
import { ProjectUsersComponent } from '../../../projects/project-user/project-users.component';
import { EditProjectModalComponent } from '../../modals/edit-project-modal/edit-project-modal.component';

@Component({
    selector: 'app-projects-content',
    templateUrl: './projects-content.component.html',
    styleUrls: ['./projects-content.component.scss'],
    standalone: true,
    imports: [
      MatButton,
      PanelTabsComponent,
      ProjectBotsComponent,
      ProjectSourcesComponent,
      ProjectUsersComponent,
      AllowedRoleDirective,
    ],
})
export class ProjectsContentComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public selectedItem: TProjectItem = {} as TProjectItem;
  private _subs: Subscription[] = [];

  public selectedTab: projectTab | undefined = undefined;
  allowedTabs = projectTab;

  readonly tabLabels: string[] = ['Project Sources', 'Agents', 'Users'];
  activeTabLabel = 'Project Sources';

  constructor(public dialog: MatDialog, private _projectsService: ProjectsService) {}

  public ngOnInit(): void {
    this._subs.push(
      this._projectsService.getProjectsObservable().subscribe(results => {
        const newProjectsData: TProjectItem | undefined = results.find(
          it => it._id === this.selectedItem._id
        );
        if (newProjectsData) {
          this.selectedItem = newProjectsData;
        }
      })
    );
    this._projectsService.setProjects();
    this.selectedTab = projectTab.SOURCES;
    this.activeTabLabel = this.tabToLabel(this.selectedTab);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedItem.previousValue !== changes.selectedItem.currentValue) {
      this.selectedItem = changes.selectedItem.currentValue;
    }
  }

  public ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
  }

  protected onTabLabelChange(label: string): void {
    this.selectedTab = this.tabLabelToTab(label);
  }

  private tabLabelToTab(label: string): projectTab {
    switch (label) {
      case 'Project Sources':
        return projectTab.SOURCES;
      case 'Agents':
        return projectTab.BOTS;
      case 'Users':
        return projectTab.USERS;
      default:
        return projectTab.SOURCES;
    }
  }

  private tabToLabel(tab: projectTab | undefined): string {
    switch (tab) {
      case projectTab.SOURCES:
        return 'Project Sources';
      case projectTab.BOTS:
        return 'Agents';
      case projectTab.USERS:
        return 'Users';
      default:
        return 'Project Sources';
    }
  }

  protected isTabVisible(tab:projectTab){
    return this.selectedTab === tab;
  }

  openEditModal(): void {
    const dialogRef = this.dialog.open(EditProjectModalComponent, {
      data: {
        name: this.selectedItem.name,
        description: this.selectedItem.description,
      },
    });

    const saveDialogSubscription = dialogRef.componentInstance.saveCallback.subscribe(response => {
      if (this.selectedItem._id) {
        this._projectsService.updateProjectDetails(this.selectedItem, response, () => {
          this.selectedItem = {
            ...this.selectedItem,
            description: response.description,
            title: response.name,
          };
        });
      }
      saveDialogSubscription.unsubscribe();
    });
  }
}
